<script setup lang="ts">
import { nextTick, ref, watch } from "vue";

/**
 * The app's one confirmation dialog — every destructive action routes through
 * it instead of firing on a single tap. Centred rather than a bottom sheet
 * (ItemActionsSheet's shape): a sheet is a menu you can dismiss by reflex, a
 * dialog is a decision, and the two must not look alike when one of them
 * deletes things.
 *
 * Flat + tonal per DESIGN.md §5: surface panel + 1px border, elevation carried
 * by the dimmed backdrop, never a box-shadow.
 */
const props = withDefaults(
  defineProps<{
    open: boolean;
    title: string;
    /** One or two sentences saying what will actually happen — never a bare "are you sure?". */
    message: string;
    confirmLabel: string;
    cancelLabel?: string;
    /** Danger styling for the confirm button (delete); false for neutral/affirmative confirms. */
    danger?: boolean;
    /** Action in flight — locks both buttons and every dismiss path. */
    busy?: boolean;
  }>(),
  { cancelLabel: "ยกเลิก", danger: false, busy: false }
);

const emit = defineEmits<{ confirm: []; cancel: [] }>();

// Focus lands on Cancel, not Confirm: an accidental Enter/Space carried in
// from the trigger must not complete a delete.
const cancelButton = ref<HTMLButtonElement | null>(null);
let previouslyFocused: HTMLElement | null = null;

function dismiss() {
  if (props.busy) return;
  emit("cancel");
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") dismiss();
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      previouslyFocused = document.activeElement as HTMLElement | null;
      window.addEventListener("keydown", onKeydown);
      await nextTick();
      cancelButton.value?.focus();
    } else {
      window.removeEventListener("keydown", onKeydown);
      previouslyFocused?.focus();
      previouslyFocused = null;
    }
  }
);
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog">
      <div v-if="open" class="fixed inset-0 z-[100] flex items-center justify-center px-6">
        <div class="absolute inset-0 bg-on-surface/40" aria-hidden="true" @click="dismiss" />
        <div
          class="dialog-panel relative w-full max-w-sm rounded-2xl border border-outline-variant bg-surface p-5"
          role="alertdialog"
          aria-modal="true"
          :aria-label="title"
          :aria-describedby="'confirm-dialog-message'"
        >
          <h2 class="font-headline-sm text-on-surface">{{ title }}</h2>
          <p id="confirm-dialog-message" class="mt-2 font-body-sm text-on-surface-variant" style="text-wrap: pretty">
            {{ message }}
          </p>

          <div class="mt-5 flex items-center gap-3">
            <button
              ref="cancelButton"
              type="button"
              class="h-11 flex-1 rounded-full bg-surface-container-high font-label-caps text-on-surface-variant transition-all active:scale-[0.98] disabled:opacity-50"
              :disabled="busy"
              @click="dismiss"
            >
              {{ cancelLabel }}
            </button>
            <button
              type="button"
              class="flex h-11 flex-1 items-center justify-center gap-2 rounded-full font-label-caps transition-all active:scale-[0.98] disabled:opacity-50"
              :class="danger ? 'bg-error-container text-on-error-container' : 'bg-primary-container text-on-primary-container'"
              :disabled="busy"
              @click="emit('confirm')"
            >
              <span v-if="busy" class="spin" aria-hidden="true" />
              {{ confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Same 200ms curve as the action sheet; the panel rises a hair instead of sliding. */
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 200ms cubic-bezier(0.25, 1, 0.5, 1);
}
.dialog-enter-active .dialog-panel,
.dialog-leave-active .dialog-panel {
  transition: transform 200ms cubic-bezier(0.25, 1, 0.5, 1);
}
.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}
.dialog-enter-from .dialog-panel,
.dialog-leave-to .dialog-panel {
  transform: scale(0.96);
}
@media (prefers-reduced-motion: reduce) {
  .dialog-enter-active .dialog-panel,
  .dialog-leave-active .dialog-panel {
    transition: none;
  }
  .dialog-enter-from .dialog-panel,
  .dialog-leave-to .dialog-panel {
    transform: none;
  }
}
</style>
